<template>
  <div
    v-if="video !== null"
    class="short-video-wrapper"
    @wheel.prevent="handleWheel"
    @touchstart="touchStart"
    @touchmove.prevent
    @touchend="touchEnd"
  >
    <transition name="el-fade-in">
      <div :key="video.videoId" class="video-bg" :style="{ backgroundImage: `url(${video.coverUrl})` }" />
    </transition>

    <div class="video-container" @click="handleSingleClick" @dblclick="handleDoubleLike">
      <video
        v-if="videoActive && currentVideoUrl"
        ref="videoPlayer"
        class="native-video"
        :src="currentVideoUrl"
        loop
        autoplay
        preload="auto"
        playsinline
        webkit-playsinline
        x5-video-player-type="h5-page"
        x5-video-player-fullscreen="true"
        @loadedmetadata="onVideoLoad"
        @waiting="onWaiting"
        @playing="onPlaying"
        @error="onVideoError"
      />

      <div class="video-status-overlay">
        <transition name="el-zoom-in-center">
          <i v-if="isLoading" class="el-icon-loading status-icon" />
          <i v-else-if="isPaused" class="el-icon-video-play status-icon" />
        </transition>
      </div>

      <div ref="likeAnimBox" class="like-anim-container" />
    </div>

    <div class="side-bar">
      <div v-if="user" class="side-item author-box" @click.stop="handleAvatarClick">
        <div class="avatar-wrapper">
          <el-avatar :size="50" :src="user.avatarUrl" />
          <div class="follow-plus"><i class="el-icon-plus" /></div>
        </div>
      </div>

      <transition name="slide-fade">
        <div v-if="showUserCard" class="user-peek-card">
          <div class="card-mask" @click="showUserCard = false" />

          <div class="card-content">
            <div class="profile-main">
              <el-avatar
                :size="80"
                :src="user.avatarUrl"
                class="card-avatar clickable-avatar"
                @click.native="goToFullProfile"
              />
              <h2 class="name">@{{ user.screenName }}</h2>
              <p class="bio">{{ user.signature || '暂无个性签名' }}</p>

              <div class="stats-group">
                <div class="stat"><b>{{ formatCount(user.fans) }}</b><span>粉丝</span></div>
                <div class="stat"><b>{{ formatCount(user.likes) }}</b><span>获赞</span></div>
              </div>

              <el-button type="danger" round class="action-btn" @click="handleFollowerUser">立即关注</el-button>
            </div>

            <div class="recent-works">
              <p class="label">近期作品</p>
              <div class="thumb-list">
                <!-- 正确的 Vue 2 循环语法是 (item, index)，而不是 {item, index} -->
                <div
                  v-for="(item, index) in userWorks"
                  :key="item.videoId || index"
                  class="work-thumb"
                  :style="{ backgroundImage: `url(${item.coverUrl})` }"
                  @click="playThisWork(item)"
                >
                  <!-- 播放小图标，通过样式居中 -->
                  <i class="el-icon-video-play" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </transition>

      <div class="side-item" @click.stop="handleLike">
        <div class="icon-circle" :class="{ 'is-active': isLiked }"><i class="el-icon-star-on" /></div>
        <span class="count-text">{{ formatCount(video.view) }}</span>
      </div>
      <div class="side-item" @click.stop="toggleComments">
        <div class="icon-circle"><i class="el-icon-s-comment" /></div>
        <span class="count-text">{{ formatCount(video.comment) }}</span>
      </div>
      <div class="side-item" @click.stop="handleShare">
        <div class="icon-circle"><i class="el-icon-share" /></div>
        <span class="count-text">{{ formatCount(video.comment) }}</span>
      </div>
    </div>

    <div class="video-info-bottom">
      <div class="info-content">
        <h3 v-if="user" class="author-name">@{{ user.screenName }}</h3>
        <p class="video-desc">{{ video.title }}</p>
        <div class="music-tag">
          <div class="music-icon-box"><i class="el-icon-headset" /></div>
          <div class="marquee-box">
            <span class="marquee-text">{{ video.title }} - 精彩原声</span>
          </div>
        </div>
      </div>
    </div>

    <div class="top-controls">
      <el-button type="info" icon="el-icon-refresh" circle size="small" :loading="isSwitching" @click="nextVideo" />
    </div>
  </div>
</template>

<script>
import { videoUrl, getShortVideo, getRecentShortVideos } from '@/api/vod'
import { doAlbumAction } from '@/api/vod'

export default {
  name: 'ShortVideo',
  data() {
    return {
      video: null,
      user: null,
      currentVideoUrl: '',
      videoActive: true,
      isLiked: false,
      isPaused: false,
      isLoading: true,
      isSwitching: false,
      isChangingSource: false, // 核心：切换状态锁
      startY: 0,
      clickTimer: null,
      showUserCard: false,
      originalVolume: 1.0,
      volumeTimer: null,
      userWorks: []
    }
  },
  watch: {
    // 监听卡片状态，触发音量变化
    showUserCard(val) {
      if (val) {
        this.fadeVolume(0.2) // 弹出时：音量降至 20%
      } else {
        this.fadeVolume(this.originalVolume) // 关闭时：恢复原音量
      }
    }
  },
  created() {
    this.getShortVideoWrapper()
  },
  methods: {
    fadeVolume(targetVolume) {
      const v = this.$refs.videoPlayer
      if (!v) return

      clearInterval(this.volumeTimer)

      // 每次调节的步长
      const step = v.volume < targetVolume ? 0.05 : -0.05

      this.volumeTimer = setInterval(() => {
        const newVolume = v.volume + step

        // 判断是否到达目标值
        if ((step > 0 && newVolume >= targetVolume) || (step < 0 && newVolume <= targetVolume)) {
          v.volume = targetVolume
          clearInterval(this.volumeTimer)
        } else {
          v.volume = Math.max(0, Math.min(1, newVolume))
        }
      }, 30) // 30ms 调节一次，体感非常平滑
    },
    async handleAvatarClick() {
      this.showUserCard = true
      // 2. 如果已经加载过该用户的作品，且 ID 没变，可以不重复加载
      // 这里假设 user 对象里有 userId
      if (this.userWorks.length > 0 && this.userWorks[0].user.userId === this.user.userId) {
        return
      }
      const queryParams = {}
      queryParams.userIdStr = this.user.userId
      getRecentShortVideos(queryParams).then(resp => {
        if (resp.code === 0) {
          this.userWorks = resp.data
        } else {
          this.$message.warning(resp.msg)
        }
      })
    },
    // 点击作品小图的处理
    playThisWork(work) {
      this.showUserCard = false
      this.prepareNewVideo(work.videoId)
    },
    // 模拟跳转完整主页
    goToFullProfile() {
      // 1. 立即关闭卡片显示
      this.showUserCard = false

      // 2. 触发恢复音量的逻辑（watch 会自动监听到 showUserCard 变为 false）
      // 但为了确保跳转瞬间声音已经开始恢复，可以手动调用一次
      this.fadeVolume(this.originalVolume || 1.0)

      // 3. 执行路由跳转
      // 使用 $nextTick 确保卡片关闭的动画开始后再跳转，或者直接跳转
      this.$router.push(`/user/${this.user.userId}`).catch(err => {
        if (err.name !== 'NavigationDuplicated') console.error(err)
      })
    },
    handleLike() {
      const jsonData = {}
      jsonData.albumId = 1
      jsonData.postId = this.video.videoId
      jsonData.action = !this.isLiked ? 1 : 2
      doAlbumAction(jsonData).then(resp => {
        if (resp.code === 0) {
          this.isLiked = !this.isLiked
          this.$message.success(resp.msg)
        } else {
          this.$message.warning(resp.msg)
        }
      })
    },
    handleShare() { this.$message.success('链接已复制') },
    toggleComments() { this.$message.info('评论暂未开启') },
    handleFollowerUser() {
      this.$message.info('follow user')
    },
    async getShortVideoWrapper() {
      if (this.isSwitching) return
      this.isSwitching = true
      this.isLoading = true

      try {
        const resp = await getShortVideo()
        if (resp.code === 0 && resp.data) {
          this.video = resp.data
          this.isLiked = false
          this.user = resp.data.user
          await this.prepareNewVideo(this.video.videoId)
        }
      } catch (err) {
        this.isSwitching = false
      }
    },

    async prepareNewVideo(videoId) {
      const resp = await videoUrl(videoId)
      if (resp.code === 0) {
        const urls = resp.data.urls
        if (urls.length === 0) {
          this.$message.error('没有视频资源 URL')
          return
        }

        const nextUrl = urls[0].url

        // 1. 开启切换锁，此时所有 Error 事件都会被静默
        this.isChangingSource = true
        this.videoActive = false

        // 2. 彻底清理旧 video
        const v = this.$refs.videoPlayer
        if (v) {
          v.pause()
          v.removeAttribute('src') // 彻底移除属性
          v.load()
        }

        this.currentVideoUrl = ''

        // 3. 增加一个微小的延迟（100ms），确保旧解码器完全释放
        setTimeout(() => {
          this.currentVideoUrl = nextUrl
          this.videoActive = true

          this.$nextTick(() => {
            const newV = this.$refs.videoPlayer
            if (newV) {
              newV.play().catch(() => { this.isPaused = true })
            }
            // 4. 视频准备播放后，延迟关闭切换锁
            setTimeout(() => {
              this.isChangingSource = false
              this.isSwitching = false
            }, 500)
          })
        }, 100)
      } else {
        this.isSwitching = false
        this.isChangingSource = false
      }
    },

    onVideoError() {
      // 核心：只要在切换过程中，或者标签不活跃，绝对不报错
      if (this.isChangingSource || !this.videoActive) return

      const v = this.$refs.videoPlayer
      if (!v || !v.error) return

      // 过滤掉 Aborted(1) 和 Network(2)
      if (v.error.code <= 2) return

      this.isLoading = false
      this.$message.error('视频资源暂不可用')
    },

    onVideoLoad() { this.isLoading = false },
    onWaiting() { this.isLoading = true },
    onPlaying() { this.isLoading = false; this.isPaused = false },
    handleSingleClick() {
      clearTimeout(this.clickTimer)
      this.clickTimer = setTimeout(() => this.togglePlay(), 250)
    },
    handleDoubleLike(e) {
      clearTimeout(this.clickTimer)
      if (!this.isLiked) this.handleLike()
      this.showLikeAnimation(e)
    },
    showLikeAnimation(e) {
      const box = this.$refs.likeAnimBox
      if (!box) return
      const heart = document.createElement('i')
      heart.className = 'el-icon-star-on heart-anim'
      heart.style.left = `${e.clientX - 40}px`
      heart.style.top = `${e.clientY - 40}px`
      box.appendChild(heart)
      setTimeout(() => heart.remove(), 800)
    },
    togglePlay() {
      const v = this.$refs.videoPlayer
      if (!v) return
      v.paused ? v.play() : v.pause()
      this.isPaused = v.paused
    },
    nextVideo() { this.getShortVideoWrapper() },
    formatCount(n) { return n > 10000 ? (n / 10000).toFixed(1) + 'w' : (n || 0) },
    handleWheel(e) {
      if (this.isSwitching || Math.abs(e.deltaY) < 50) return
      this.nextVideo()
    },
    touchStart(e) { this.startY = e.touches[0].clientY },
    touchEnd(e) {
      const diff = this.startY - e.changedTouches[0].clientY
      if (!this.isSwitching && Math.abs(diff) > 80) this.nextVideo()
    }
  }
}
</script>

<style scoped>
.short-video-wrapper {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  background: #000;
  overflow: hidden;
  z-index: 999;
}

.video-bg {
  position: absolute;
  inset: -10%;
  background-size: cover;
  background-position: center;
  filter: blur(40px) brightness(0.3);
  z-index: 0;
}

.video-container {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.native-video {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

@media screen and (max-width: 768px) {
  .native-video { object-fit: cover; }
}

.video-info-bottom {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  background: linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 100%);
  padding: 40px 15px 35px 15px;
  z-index: 10;
  pointer-events: none;
  box-sizing: border-box;
}

.info-content {
  margin-right: 80px;
  color: #fff;
  text-align: left;
}

.author-name {
  font-size: 17px;
  font-weight: 600;
  margin-bottom: 8px;
  text-shadow: 0 1px 2px rgba(0,0,0,0.5);
}

.video-desc {
  font-size: 15px;
  line-height: 1.5;
  color: #fff;
  pointer-events: auto;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
}

.music-tag {
  margin-top: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.side-bar {
  position: absolute;
  right: 12px;
  bottom: 15%;
  z-index: 11;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

/* 其他样式同前... */
.icon-circle { width: 48px; height: 48px; background: rgba(255, 255, 255, 0.15); border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 26px; color: #fff; backdrop-filter: blur(5px); }
.icon-circle.is-active { color: #fe2c55; }
.count-text { font-size: 12px; margin-top: 4px; color: #fff; }
.avatar-wrapper { border: 2px solid #fff; border-radius: 50%; position: relative; }
.follow-plus { position: absolute; bottom: -8px; left: 50%; transform: translateX(-50%); background: #fe2c55; border-radius: 50%; width: 18px; height: 18px; display: flex; align-items: center; justify-content: center; font-size: 10px; color: #fff; }
.video-status-overlay { position: absolute; z-index: 2; pointer-events: none; }
.status-icon { font-size: 70px; color: rgba(255, 255, 255, 0.4); }
@keyframes rotate { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes marquee { 0% { transform: translateX(100%); } 100% { transform: translateX(-100%); } }
.music-icon-box { animation: rotate 4s linear infinite; }
.marquee-box { overflow: hidden; white-space: nowrap; width: 150px; }
.marquee-text { display: inline-block; animation: marquee 8s linear infinite; font-size: 13px; }
.like-anim-container { position: absolute; inset: 0; pointer-events: none; z-index: 5; }
::v-deep .heart-anim { position: absolute; font-size: 80px; color: #fe2c55; animation: heartFly 0.8s ease-out forwards; }
@keyframes heartFly { 0% { transform: scale(1); opacity: 0.8; } 100% { transform: translateY(-150px) scale(2); opacity: 0; } }
.top-controls { position: absolute; top: 20px; left: 20px; z-index: 10; }

.user-peek-card {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  justify-content: flex-end;
}

.card-mask {
  flex: 1;
  background: rgba(0, 0, 0, 0.1); /* 极轻微的遮罩 */
}

.card-content {
  width: 75%;
  height: 100%;
  background: rgba(15, 15, 15, 0.75); /* 深色半透明 */
  backdrop-filter: blur(20px) saturate(150%); /* 磨砂玻璃效果 */
  -webkit-backdrop-filter: blur(20px) saturate(150%);
  border-left: 1px solid rgba(255, 255, 255, 0.1);
  color: #fff;
  padding: 80px 24px 40px;
  display: flex;
  flex-direction: column;
  box-shadow: -10px 0 30px rgba(0, 0, 0, 0.5);
}

/* 进场动画：右侧滑入 + 柔和淡入 */
.slide-fade-enter-active, .slide-fade-leave-active {
  transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
}
.slide-fade-enter, .slide-fade-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

/* 内部元素样式优化 */
.profile-main {
  text-align: center;
  margin-bottom: 40px;
}
.card-avatar {
  border: 2px solid #fe2c55;
  margin-bottom: 16px;
}
.stats-group {
  display: flex;
  justify-content: center;
  gap: 40px;
  margin: 20px 0;
}
.stat {
  display: flex;
  flex-direction: column;
}
.stat b { font-size: 18px; }
.stat span { font-size: 12px; color: rgba(255,255,255,0.5); }

.recent-works .thumb-list {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}
.work-thumb {
  flex: 1;
  aspect-ratio: 3/4;
  background: rgba(255,255,255,0.1);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255,255,255,0.3);
}

.footer-link {
  margin-top: auto;
  text-align: center;
  font-size: 14px;
  color: rgba(255,255,255,0.6);
  padding: 15px;
  border-top: 1px solid rgba(255,255,255,0.05);
}

.clickable-avatar {
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.clickable-avatar:hover {
  transform: scale(1.05);
  box-shadow: 0 0 15px rgba(254, 44, 85, 0.5); /* 淡淡的红晕，呼应主题色 */
}

.clickable-avatar:active {
  transform: scale(0.95);
}

.name {
  cursor: pointer;
  margin-top: 10px;
}

.name:hover {
  text-decoration: underline;
  opacity: 0.8;
}

.recent-works .thumb-list {
  display: flex;
  gap: 8px;
  margin-top: 12px;
  overflow-x: auto; /* 如果作品多的话，支持横向滚动 */
  padding-bottom: 4px;
}

/* 隐藏滚动条（可选，保持界面整洁） */
.recent-works .thumb-list::-webkit-scrollbar {
  display: none;
}

.work-thumb {
  flex: 0 0 30%; /* 固定每个缩略图占宽度的 30%，防止被挤压 */
  aspect-ratio: 3/4;
  background-color: rgba(255, 255, 255, 0.1);
  background-size: cover;       /* 核心：让封面图撑满容器并居中裁剪 */
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  transition: transform 0.2s, filter 0.2s;
}

.work-thumb:hover {
  transform: scale(1.02);
  filter: brightness(1.1); /* 悬停时稍微变亮 */
}

.work-thumb .el-icon-video-play {
  font-size: 20px;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.5); /* 给白色图标加阴影，防止遇到亮色封面时看不清 */
}
</style>
